import { CircleAlert, CircleX } from 'lucide-react';
import type { TaskFailedRun, TaskPickupSkip } from '@shared/types/tasks';
import type { RunSummary } from '@shared/types/factory';
import { StatusBadge } from '@/components/StatusBadge';
import { formatDateTime, formatRelative } from '@/lib/formatters';
import { cn } from '@/lib/utils';
import { pickupSkipSentence, reasonSentence } from './task-waiting';

/**
 * T-65: why the AI builder hasn't picked a task up yet (every machine busy, the earlier run's machine
 * full, no machine for the project…), from its latest try. Gone once a run starts.
 */

/** On the card: a few words, the full reason on hover; `detail` follows it there (SF-T-278: the sub-tasks' runs). */
export function PickupSkipBadge({ skip, detail }: { skip: TaskPickupSkip; detail?: string }) {
  return (
    <StatusBadge
      size="sm"
      tone="warning"
      className="max-w-full"
      data-hint={`The AI builder hasn't started: ${pickupSkipSentence(skip)} Since ${formatDateTime(skip.since, 'short')}.${detail ? ` ${detail}` : ''}`}
      data-testid="task-card-pickup-skip"
    >
      <CircleAlert aria-hidden />
      <span className="truncate">{skip.short}</span>
    </StatusBadge>
  );
}

/** In the task panel, under Waiting on: the full sentence and since when. */
export function PickupSkipNote({ skip, className }: { skip: TaskPickupSkip; className?: string }) {
  return (
    <p className={cn('flex items-start gap-1.5 text-xs text-warning-strong', className)} data-testid="task-pickup-skip">
      <CircleAlert className="mt-px size-3.5 shrink-0" aria-hidden />
      <span>
        Not started yet: {pickupSkipSentence(skip)}{' '}
        <span className="text-muted-foreground" data-hint={formatRelative(skip.since)}>
          Since {formatDateTime(skip.since, 'short')}.
        </span>
      </span>
    </p>
  );
}

/**
 * T-28: the run going on the task is on a machine near its Claude weekly limit (or every machine with
 * room was): a few words, the full reason on hover. On the card and beside the task's latest run.
 */
export function RunLimitBadge({ warning }: { warning: NonNullable<RunSummary['limit_warning']> }) {
  return (
    <StatusBadge
      size="sm"
      tone="warning"
      className="max-w-full"
      data-hint={`${reasonSentence(warning.reason)} The run may stop when the limit runs out.`}
      data-testid="task-run-limit-warning"
    >
      <CircleAlert aria-hidden />
      <span className="truncate">{warning.short}</span>
    </StatusBadge>
  );
}

/**
 * SF-T-206: the task's newest run failed. On the card until a newer run starts; the failed run's
 * error on hover. The task waits on whoever started the run, who opens it to see why.
 */
export function FailedRunBadge({ run }: { run: TaskFailedRun }) {
  return (
    <StatusBadge
      size="sm"
      tone="danger"
      className="max-w-full"
      data-hint={`R-${run.number} failed${run.error ? `: ${run.error}` : '.'} Open the task to see why, then ask the AI builder again or carry on yourself.`}
      data-testid="task-card-failed-run"
    >
      <CircleX aria-hidden />
      <span className="truncate">Run failed</span>
    </StatusBadge>
  );
}
